import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FormErrorsHelper } from './AnotaFormErrorsHelper';

type ValidationErrorResponse = ValidationErrors | null;

type CustomOrValidation<T> = T extends String ? ValidatorFn : ValidationErrorResponse;

/**
 * This is an inherited class from common Angular {@link Validators} methods with default messages
 *
 * @example AnotaValidators.required('Hey dude, this field is required!')
 *
 * Custom messages can be built for validators with params, like min or max
 *
 * @example AnotaValidators.min(3, "This field can't be less than {min}");
 * then "This field can\'t be less than 3" will be the result.
 */
export class AnotaValidators {
  static required<T>(param: T): CustomOrValidation<T> {
    if (typeof param === 'string') {
      return AnotaValidators.requiredValidator.bind(param);
    }
    return AnotaValidators.requiredValidator(param as AbstractControl) as CustomOrValidation<T>;
  }

  private static requiredValidator(control: AbstractControl): ValidationErrorResponse {
    const result = Validators.required(control);

    const message = typeof this === 'string' ? this : 'Este campo é obrigatório';

    if (result) {
      result['message'] = message;
    }

    return result;
  }

  /** Available replace keys
   *
   * @{key}: key to be compared
   *
   * @{name}: name of compared field
   */
  static equal<T extends { [K in keyof T]: AbstractControl<any, any> }>(
    key: keyof T,
    errorMessage: string = 'Os valores de {name} e {key} devem ser iguais',
  ): ValidatorFn {
    let hasValidated = false;

    return function (control: AbstractControl) {
      let result: ValidationErrorResponse = null;
      const fieldName = AnotaValidators._getControlName(control);

      const controlToCompare = (control.parent?.controls as any)?.[key];
      const valueToCompare = controlToCompare?.value;

      if (!hasValidated && controlToCompare) {
        hasValidated = true;
        controlToCompare.valueChanges.subscribe(() => {
          control.updateValueAndValidity({ emitEvent: false });
        });
      }

      if (control.value !== valueToCompare) {
        result = { equal: true };
      }

      if (result) {
        result['message'] = errorMessage.replace(/{name}/gi, fieldName || '').replace(/{key}/gi, String(key));
      }

      return result;
    };
  }

  /** Available replace keys
   *
   * @{min}: required min value
   */
  static min(
    min: number,
    errorMessage: string = 'O valor deste campo não pode ser inferior a {min}',
  ): ValidatorFn {
    return function (control: AbstractControl) {
      const result = Validators.min(min)(control);
      if (result?.['min']) {
        result['message'] = errorMessage.replace(/{min}/gi, String(min));
      }

      return result;
    };
  }

  /** Available replace keys
   *
   * @{max}: required max value
   */
  static max(
    max: number,
    errorMessage: string = 'O valor deste campo não pode ser superior a {max}',
  ): ValidatorFn {
    return function (control: AbstractControl) {
      const result = Validators.max(max)(control);

      if (result?.['max']) {
        result['message'] = errorMessage.replace(/{max}/gi, String(max));
      }

      return result;
    };
  }

  /** Available replace keys
   *
   * @{requiredLength}: required value length
   *
   * @{actualLength}: the actual value provided
   */
  static minLength = function (
    min: number,
    errorMessage: string = 'Este campo deve conter ao menos {requiredLength} caractere(s)',
  ): ValidatorFn {
    return function (control: AbstractControl) {
      let result = Validators.minLength(min)(control);

      if (result?.['minlength']) {
        const { actualLength, requiredLength } = result!['minlength'];

        result['message'] = errorMessage
          .replace(/{requiredLength}/gi, String(requiredLength))
          .replace(/{actualLength}/gi, String(actualLength));
      }

      return result;
    };
  };

  /** Available replace keys
   *
   * @{requiredLength}: required value length
   *
   * @{actualLength}: the actual value provided
   */
  static maxLength = function (
    max: number,
    errorMessage: string = 'Este campo deve conter até {requiredLength} caracteres',
  ): ValidatorFn {
    return function (control: AbstractControl) {
      let result = Validators.maxLength(max)(control);

      if (result?.['maxlength']) {
        const { actualLength, requiredLength } = result!['maxlength'];
        result = {
          message: errorMessage
            .replace(/{requiredLength}/gi, String(requiredLength))
            .replace(/{actualLength}/gi, String(actualLength)),
        };
      }

      return result;
    };
  };

  /** E-mail validation */
  static email(param: AbstractControl | string) {
    if (typeof param === 'string') {
      return AnotaValidators.emailValidator.bind(param);
    }
    return AnotaValidators.emailValidator(param);
  }

  private static emailValidator(control: AbstractControl) {
    // Expressão regular melhorada para validar e-mails corretamente
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const email = control.value;
    const isValid = emailPattern.test(email);

    const message = typeof this === 'string' ? this : 'E-mail inválido';

    return isValid ? null : { email: true, message };
  }


  /** validate if string starts with provided value
   * @{value}: provided start string
   */
  static startsWith(value: string, message: string = `O texto fornecido deve iniciar com "{value}"`) {
    return (control: AbstractControl) => {
      if (control?.value && String(control.value).startsWith(value)) {
        return null;
      }

      return {
        startsWith: true,
        message: message.replace(/{value}/gi, value),
      };
    };
  }

  /** Brazilian CPF or CNPJ document pattern validation */
  static cpfcnpj(param: AbstractControl | string) {
    if (typeof param === 'string') {
      return AnotaValidators.validateCPFCNPJ.bind(param);
    }
    return AnotaValidators.validateCPFCNPJ(param);
  }

  /** Brazilian CPF document pattern validation */
  static cpf(param: AbstractControl | string) {
    if (typeof param === 'string') {
      return (control: AbstractControl) => {
        if (AnotaValidators.CPFValidator(control.value)) {
          return null;
        }

        return { cpf: true, message: AnotaValidators.INVALID_CPF_MSG };
      };
    }

    if (AnotaValidators.CPFValidator(param.value)) {
      return null;
    }

    return { cpf: true, message: AnotaValidators.INVALID_CPF_MSG };
  }

  /** Brazilian CNPJ document pattern validation */
  static cnpj(param: AbstractControl | string) {
    if (typeof param === 'string') {
      return (control: AbstractControl) => {
        if (AnotaValidators.CNPJValidator(control.value)) {
          return null;
        }

        return { cnpj: true, message: AnotaValidators.INVALID_CNPJ_MSG };
      };
    }

    if (AnotaValidators.CNPJValidator(param.value)) {
      return null;
    }

    return { cnpj: true, message: AnotaValidators.INVALID_CNPJ_MSG };
  }

  private static readonly CPF_LENGTH = 11;
  private static readonly CNPJ_LENGTH = 14;
  private static readonly INVALID_CPF_MSG = 'CPF inválido';
  private static readonly INVALID_CNPJ_MSG = 'CNPJ inválido';

  private static _buildDigit(arr: (string | number)[]): number {
    const isCpf = arr.length < AnotaValidators.CPF_LENGTH;
    const digit =
      arr
        .map((val, idx) => AnotaValidators._cnpjCharToValue(val) * ((!isCpf ? idx % 8 : idx) + 2))
        .reduce((total, current) => total + current) % AnotaValidators.CPF_LENGTH;

    return digit < 2 ? 0 : AnotaValidators.CPF_LENGTH - digit;
  }

  /**
   * Maps an alphanumeric CNPJ character to its numeric value for check-digit computation.
   * Digits 0–9 map to 0–9; letters A–Z map to 10–35 (per RFB Instrução Normativa 2229/2024).
   */
  private static _cnpjCharToValue(val: string | number): number {
    if (typeof val === 'number') return val;
    const code = val.toUpperCase().charCodeAt(0);
    if (code >= 48 && code <= 57) return code - 48; // '0'–'9'
    if (code >= 65 && code <= 90) return code - 55; // 'A'–'Z' → 10–35
    return NaN;
  }

  private static _validateDocumentPattern(cpfCnpj: string): boolean {
    if (/^([0-9A-Z])\1*$/i.test(cpfCnpj)) {
      return false;
    }

    const cpfCnpjArr: (string | number)[] = cpfCnpj.split('').reverse().slice(2);

    cpfCnpjArr.unshift(AnotaValidators._buildDigit(cpfCnpjArr));
    cpfCnpjArr.unshift(AnotaValidators._buildDigit(cpfCnpjArr));

    if (cpfCnpj !== cpfCnpjArr.reverse().join('')) {
      return false;
    }
    return true;
  }

  private static CPFValidator(value: string): boolean {
    if (value.length !== AnotaValidators.CPF_LENGTH) {
      return false;
    }

    if (!AnotaValidators._validateDocumentPattern(value)) {
      return false;
    }

    return true;
  }

  private static CNPJValidator(value: string): boolean {
    if (value.length !== AnotaValidators.CNPJ_LENGTH) {
      return false;
    }

    if (!/^[A-Z0-9]{12}\d{2}$/i.test(value)) {
      return false;
    }

    if (!AnotaValidators._validateDocumentPattern(value)) {
      return false;
    }

    return true;
  }

  private static validateCPFCNPJ(c: AbstractControl): ValidationErrors | null {
    const cpfCnpj = c.value?.replace(/[.\-\/\s]/g, '').toUpperCase() || '';

    if (!cpfCnpj) {
      return null;
    }

    const defaultMessage =
      cpfCnpj.length > 11
        ? AnotaValidators.INVALID_CNPJ_MSG
        : AnotaValidators.INVALID_CPF_MSG;

    const message = typeof this === 'string' ? this : defaultMessage;

    if (cpfCnpj.length === this.CNPJ_LENGTH && this.CNPJValidator(cpfCnpj)) {
      return null;
    }

    if (cpfCnpj.length === this.CPF_LENGTH && this.CPFValidator(cpfCnpj)) {
      return null;
    }

    return { cpfCnpjInvalid: true, message };
  }

  private static URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/; // prettier-ignore

  static url(param: AbstractControl | string) {
    if (typeof param === 'string') {
      return AnotaValidators.urlValidator.bind(param);
    }
    return AnotaValidators.urlValidator(param);
  }

  private static urlValidator(control: AbstractControl) {
    const message = typeof this === 'string' ? this : 'Link inválido';

    if (control?.value) {
      const match = this.URL_REGEX.test(control.value);
      if (match) {
        return null;
      }
    }

    return {
      url: true,
      message,
    };
  }

  static formGroup(param: AbstractControl | string) {
    if (param && typeof param !== 'string') {
      return FormErrorsHelper.getFormGroupErrors(param as FormGroup);
    }

    return (control: AbstractControl) => {
      return FormErrorsHelper.getFormGroupErrors(control as FormGroup);
    };
  }

  private static _getControlName(control: AbstractControl): string | null {
    const formGroup = control.parent?.controls || {};
    return Object.keys(formGroup).find(name => control === formGroup[name as never]) || null;
  }

  static charactersValidator(control: AbstractControl) {
    const pattern = /^[a-zA-ZÀ-ÿ0-9\s]+$/;
    if (control.value && !pattern.test(control.value)) {
      return { message: 'Este campo contém caracteres inválidos' };
    }

    return null;
  }
}
